import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Form, Badge, Alert } from 'react-bootstrap';
import { formatFechaPartido } from '../../utils/fechaUtils';

const CoachAsignarProPoints = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [partido, setPartido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [alertMessage, setAlertMessage] = useState(null);
  
  // Estados para evaluaciones
  const [evaluaciones, setEvaluaciones] = useState({});
  const [equipoGanador, setEquipoGanador] = useState(null);
  const [asistenciaMasiva, setAsistenciaMasiva] = useState({
    miEquipo: false,
    equipoRival: false
  });

  const isMobile = window.innerWidth <= 768;

  // Logros individuales con sus imágenes
  const logrosIndividuales = [
    { key: 'destacado', nombre: 'Destacado', puntos: 2, imagen: '/images/jugadorcampo-1x.jpeg' },
    { key: 'el_jugon', nombre: 'El Jugón', puntos: 3, imagen: '/images/eljugon-1x.jpeg' },
    { key: 'el_cerrojo', nombre: 'El Cerrojo', puntos: 3, imagen: '/images/cerrojo-1x.jpeg' },
    { key: 'el_lider', nombre: 'El Líder', puntos: 3, imagen: '/images/lider-1x.jpeg' },
    { key: 'el_mejor_companero', nombre: 'El Mejor Compañero', puntos: 3, imagen: '/images/mejorcompanero-1x.jpeg' },
    { key: 'el_machete', nombre: 'El Desequilibrante', puntos: 3, imagen: '/images/desequilibrante-1x.jpeg' },
    { key: 'jugador_de_equipo', nombre: 'Jugador de Equipo', puntos: 3, imagen: '/img/1x/jugadordeequipo.jpeg' }
  ];

  // Nota: Los logros especiales se asignan por separado, no en partidos individuales

  // Cargar detalles del partido
  useEffect(() => {
    const fetchPartidoDetalles = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('prokids_token');

        const response = await fetch(`/api/coach/partidos/${id}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.success) {
            setPartido(result.data);
            initializeEvaluaciones(result.data);
          } else {
            setError('Error al cargar detalles del partido');
          }
        } else {
          setError(`Error del servidor: ${response.status}`);
        }
      } catch (error) {
        setError('Error de conexión al cargar detalles del partido');
        console.error('Error al cargar detalles del partido:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchPartidoDetalles();
    }
  }, [id]);

  // Inicializar evaluaciones para todos los jugadores
  const initializeEvaluaciones = (partidoData) => {
    const evaluacionesIniciales = {};
    
    // Inicializar para mi equipo
    if (partidoData.miEquipo) {
      partidoData.miEquipo.jugadores.forEach(jugador => {
        evaluacionesIniciales[jugador.id] = {
          futbolista_id: jugador.id,
          asistencia: false,
          victoria: false,
          destacado: false,
          el_jugon: false,
          el_cerrojo: false,
          el_lider: false,
          el_mejor_companero: false,
          el_machete: false,
          jugador_de_equipo: false,
          especial: false,
          observaciones: ''
        };
      });
    }

    // Inicializar para equipo rival
    if (partidoData.equipoRival) {
      partidoData.equipoRival.jugadores.forEach(jugador => {
        evaluacionesIniciales[jugador.id] = {
          futbolista_id: jugador.id,
          asistencia: false,
          victoria: false,
          destacado: false,
          el_jugon: false,
          el_cerrojo: false,
          el_lider: false,
          el_mejor_companero: false,
          el_machete: false,
          jugador_de_equipo: false,
          especial: false,
          observaciones: ''
        };
      });
    }

    setEvaluaciones(evaluacionesIniciales);
  };

  // Manejar asistencia masiva
  const handleAsistenciaMasiva = (equipo, checked) => {
    setAsistenciaMasiva(prev => ({ ...prev, [equipo]: checked }));

    const jugadores = equipo === 'miEquipo' ? partido.miEquipo.jugadores : partido.equipoRival.jugadores;

    setEvaluaciones(prev => {
      const nuevas = { ...prev };
      jugadores.forEach(jugador => {
        if (nuevas[jugador.id]) {
          nuevas[jugador.id].asistencia = checked;
        }
      });
      return nuevas;
    });
  };

  // Manejar selección de equipo ganador
  const handleEquipoGanador = (equipoId) => {
    setEquipoGanador(equipoId);

    setEvaluaciones(prev => {
      const nuevas = { ...prev };

      // Resetear victoria para todos
      Object.keys(nuevas).forEach(jugadorId => {
        nuevas[jugadorId].victoria = false;
      });

      // Asignar victoria al equipo ganador (solo si NO es empate)
      if (equipoId && equipoId !== 'empate') {
        const equipoGanadorData = equipoId === partido.miEquipo.id ? partido.miEquipo : partido.equipoRival;
        equipoGanadorData.jugadores.forEach(jugador => {
          if (nuevas[jugador.id]) {
            nuevas[jugador.id].victoria = true;
          }
        });
      }

      return nuevas;
    });
  };

  // Manejar cambio en evaluación individual
  const handleEvaluacionChange = (jugadorId, campo, valor) => {
    setEvaluaciones(prev => {
      const nuevas = { ...prev };

      // Logros exclusivos por partido (solo uno por partido)
      const logrosExclusivos = ['el_jugon', 'el_cerrojo', 'el_lider', 'el_mejor_companero', 'el_machete', 'jugador_de_equipo'];

      // Destacado: máximo 3 por EQUIPO
      if (campo === 'destacado' && valor === true) {
        // Determinar a qué equipo pertenece el jugador
        const esMiEquipo = partido.miEquipo?.jugadores.some(j => j.id === jugadorId);
        const esEquipoRival = partido.equipoRival?.jugadores.some(j => j.id === jugadorId);

        // Obtener IDs de jugadores del mismo equipo
        let jugadoresDelMismoEquipo = [];
        if (esMiEquipo) {
          jugadoresDelMismoEquipo = partido.miEquipo.jugadores.map(j => j.id);
        } else if (esEquipoRival) {
          jugadoresDelMismoEquipo = partido.equipoRival.jugadores.map(j => j.id);
        }

        // Contar destacados actuales en el mismo equipo
        const destacadosEnEquipo = jugadoresDelMismoEquipo.filter(
          id => id !== jugadorId && nuevas[id]?.destacado
        ).length;

        if (destacadosEnEquipo >= 3) {
          // No permitir más de 3 destacados por equipo
          const nombreEquipo = esMiEquipo ? partido.miEquipo.nombre : partido.equipoRival.nombre;
          setAlertMessage(`⚠️ Máximo 3 jugadores destacados por equipo (${nombreEquipo})`);
          setTimeout(() => setAlertMessage(null), 3000);
          return prev;
        }
      }

      // Si es un logro exclusivo y se está marcando como true
      if (valor === true && logrosExclusivos.includes(campo)) {
        // Desmarcar este logro de todos los demás jugadores
        Object.keys(nuevas).forEach(otroJugadorId => {
          if (otroJugadorId !== jugadorId && nuevas[otroJugadorId][campo]) {
            nuevas[otroJugadorId] = {
              ...nuevas[otroJugadorId],
              [campo]: false
            };
          }
        });
      }

      // Actualizar el jugador actual
      nuevas[jugadorId] = {
        ...nuevas[jugadorId],
        [campo]: valor
      };

      return nuevas;
    });
  };

  // Función para formatear hora
  const formatearHora = (hora) => {
    if (!hora) return 'Hora no disponible';
    return hora.substring(0, 5); // HH:MM
  };

  // Función para obtener color del estado
  const getEstadoColor = (estado) => {
    const colores = {
      'preorganizado': 'warning',
      'confirmado': 'success',
      'finalizado': 'secondary',
      'suspendido': 'danger'
    };
    return colores[estado] || 'secondary';
  };

  // Función para obtener texto de posición
  const getPosicionTexto = (posicion) => {
    const posiciones = {
      1: 'Portero',
      2: 'Defensa',
      3: 'Centrocampista',
      4: 'Delantero'
    };
    return posiciones[posicion] || 'Sin posición';
  };

  // Función para obtener color de fondo según posición
  const getPosicionColor = (posicion) => {
    const colores = {
      1: '#e8f5e8', // Portero - Verde claro
      2: '#e3f2fd', // Defensa - Azul claro
      3: '#fff3e0', // Centrocampista - Naranja claro
      4: '#fce4ec'  // Delantero - Rosa claro
    };
    return colores[posicion] || '#f8f9fa';
  };

  // Función para obtener color de borde según posición
  const getPosicionBorderColor = (posicion) => {
    const colores = {
      1: '#4caf50', // Portero - Verde
      2: '#2196f3', // Defensa - Azul
      3: '#ff9800', // Centrocampista - Naranja
      4: '#e91e63'  // Delantero - Rosa
    };
    return colores[posicion] || '#e1e5e9';
  };

  // Función para ordenar jugadores por posición
  const ordenarJugadoresPorPosicion = (jugadores) => {
    return [...jugadores].sort((a, b) => {
      if (a.posicionAsignada !== b.posicionAsignada) {
        return (a.posicionAsignada || 5) - (b.posicionAsignada || 5);
      }
      if (a.esTitular !== b.esTitular) {
        if (a.esTitular === true) return -1;
        if (b.esTitular === true) return 1;
      }
      return a.nombre.localeCompare(b.nombre);
    });
  };

  // Validar que se haya completado la información del partido
  const validarActaCompleta = () => {
    const errores = [];

    // Verificar que se haya seleccionado resultado (ganador o empate)
    if (!equipoGanador) {
      errores.push('Debe seleccionar el resultado del partido (ganador o empate)');
    }

    // Verificar asistencias del equipo rival
    const jugadoresRival = partido.equipoRival?.jugadores || [];
    const asistenciasRival = jugadoresRival.filter(jugador =>
      evaluaciones[jugador.id]?.asistencia
    ).length;

    if (asistenciasRival === 0 && jugadoresRival.length > 0) {
      errores.push('Debe marcar al menos una asistencia del equipo rival');
    }

    return errores;
  };

  // Guardar evaluaciones
  const handleGuardarEvaluaciones = async () => {
    try {
      setSaving(true);

      // Validar acta completa
      const erroresValidacion = validarActaCompleta();
      if (erroresValidacion.length > 0) {
        setError(`Acta incompleta:\n${erroresValidacion.join('\n')}`);
        setSaving(false);
        return;
      }

      const token = localStorage.getItem('prokids_token');

      // Preparar datos para enviar
      const evaluacionesArray = Object.entries(evaluaciones)
        .filter(([, evaluacion]) =>
          evaluacion.asistencia || evaluacion.victoria || evaluacion.destacado || evaluacion.el_jugon ||
          evaluacion.el_cerrojo || evaluacion.el_lider || evaluacion.el_mejor_companero ||
          evaluacion.el_machete || evaluacion.jugador_de_equipo
        )
        .map(([jugadorId, evaluacion]) => ({
          futbolista_id: parseInt(jugadorId),
          ...evaluacion
        }));

      const response = await fetch('/api/coach/evaluaciones-masivas', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          partido_id: parseInt(id),
          evaluaciones: evaluacionesArray,
          equipo_ganador: equipoGanador === 'empate' ? null : equipoGanador
        })
      });

      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setShowConfirm(false);
          navigate(`/coach/partidos/${id}`, {
            state: {
              message: result.message || 'ProPoints asignados exitosamente y partido finalizado',
              type: 'success'
            }
          });
        } else {
          setError('Error al guardar evaluaciones');
        }
      } else {
        setError(`Error del servidor: ${response.status}`);
      }
    } catch (error) {
      setError('Error de conexión al guardar evaluaciones');
      console.error('Error al guardar evaluaciones:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh'
      }}>
        <div style={{ textAlign: 'center', color: '#4352A9' }}>
          <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⭐</div>
          <p>Cargando sistema ProPoints...</p>
        </div>
      </div>
    );
  }

  // Verificar si el partido está finalizado (con limpieza de caracteres extraños)
  const estadoLimpio = partido?.estado?.trim().replace(/\0/g, '');
  const partidoFinalizado = estadoLimpio === 'finalizado';

  if (partidoFinalizado) {
    return <PartidoFinalizadoView partido={partido} navigate={navigate} />;
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '40px 20px' : '60px 40px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>❌</div>
        <h3 style={{
          fontSize: isMobile ? '1.2rem' : '1.4rem',
          fontWeight: '600',
          color: '#dc3545',
          margin: '0 0 8px 0'
        }}>
          Error al cargar ProPoints
        </h3>
        <p style={{
          fontSize: isMobile ? '14px' : '15px',
          color: '#6c757d',
          margin: '0 0 24px 0'
        }}>
          {error}
        </p>
        <Button
          variant="primary"
          onClick={() => navigate('/coach/partidos')}
          style={{
            backgroundColor: '#4352A9',
            borderColor: '#4352A9'
          }}
        >
          Volver a Mis Partidos
        </Button>
      </div>
    );
  }

  if (!partido) {
    return (
      <div style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: isMobile ? '40px 20px' : '60px 40px',
        textAlign: 'center',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e1e5e9'
      }}>
        <div style={{ fontSize: '2rem', marginBottom: '16px' }}>🔍</div>
        <h3 style={{
          fontSize: isMobile ? '1.2rem' : '1.4rem',
          fontWeight: '600',
          color: '#6c757d',
          margin: '0 0 8px 0'
        }}>
          Partido no encontrado
        </h3>
        <Button
          variant="primary"
          onClick={() => navigate('/coach/partidos')}
          style={{
            backgroundColor: '#4352A9',
            borderColor: '#4352A9'
          }}
        >
          Volver a Mis Partidos
        </Button>
      </div>
    );
  }

  return (
    <div style={{
      padding: isMobile ? '20px' : '24px',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: isMobile ? '20px' : '24px'
      }}>
        <div>
          <h1 style={{
            fontSize: isMobile ? '1.5rem' : '2rem',
            fontWeight: '700',
            color: '#061156',
            margin: 0
          }}>
            ⭐ Asignar ProPoints
          </h1>
          <p style={{
            fontSize: isMobile ? '14px' : '16px',
            color: '#6c757d',
            margin: '4px 0 0 0'
          }}>
            Partido #{partido.id} - {partido.modalidadTexto} - {formatFechaPartido(partido.fecha, partido.jornada.fecha)}
          </p>

          {alertMessage && (
            <Alert variant="warning" style={{ marginTop: '16px', marginBottom: '0' }}>
              {alertMessage}
            </Alert>
          )}

          {error && (
            <Alert variant="danger" style={{ marginTop: '16px', marginBottom: '0', whiteSpace: 'pre-line' }}>
              {error}
            </Alert>
          )}
        </div>
        <Button
          variant="outline-primary"
          onClick={() => navigate(`/coach/partidos/${id}`)}
          style={{
            borderColor: '#4352A9',
            color: '#4352A9'
          }}
        >
          ← Volver
        </Button>
      </div>

      {/* Información del partido */}
      <Card style={{
        marginBottom: isMobile ? '20px' : '24px',
        border: '1px solid #e1e5e9',
        borderRadius: '12px'
      }}>
        <Card.Body style={{ padding: isMobile ? '20px' : '24px' }}>
          <Row>
            <Col md={6}>
              <h5 style={{ color: '#061156', marginBottom: '16px' }}>📅 Información del Partido</h5>
              <div style={{ marginBottom: '12px' }}>
                <strong>Fecha:</strong> {formatFechaPartido(partido.fecha, partido.jornada.fecha)}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Hora:</strong> {formatearHora(partido.hora || partido.jornada.hora)}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Estado:</strong>{' '}
                <Badge bg={getEstadoColor(partido.estado)}>
                  {partido.estadoTexto}
                </Badge>
              </div>
            </Col>
            <Col md={6}>
              <h5 style={{ color: '#061156', marginBottom: '16px' }}>📍 Sede</h5>
              <div style={{ marginBottom: '12px' }}>
                <strong>Nombre:</strong> {partido.sede.nombre}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <strong>Población:</strong> {partido.sede.poblacion}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Estado del acta */}
      <Card style={{
        marginBottom: isMobile ? '16px' : '20px',
        border: '1px solid #e0e0e0',
        borderRadius: '12px',
        backgroundColor: '#f8f9fa'
      }}>
        <Card.Body style={{ padding: '16px' }}>
          <h6 style={{ color: '#061156', marginBottom: '12px' }}>📋 Estado del Acta</h6>
          <Row>
            <Col md={4}>
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                {equipoGanador ? '✅' : '❌'} Equipo ganador: {equipoGanador ? 'Seleccionado' : 'Pendiente'}
              </div>
            </Col>
            <Col md={4}>
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                {(() => {
                  const jugadoresRival = partido.equipoRival?.jugadores || [];
                  const asistenciasRival = jugadoresRival.filter(jugador =>
                    evaluaciones[jugador.id]?.asistencia
                  ).length;
                  return asistenciasRival > 0 ? '✅' : '❌';
                })()} Asistencias rival: {(() => {
                  const jugadoresRival = partido.equipoRival?.jugadores || [];
                  const asistenciasRival = jugadoresRival.filter(jugador =>
                    evaluaciones[jugador.id]?.asistencia
                  ).length;
                  return `${asistenciasRival}/${jugadoresRival.length}`;
                })()}
              </div>
            </Col>
            <Col md={4}>
              <div style={{ fontSize: '14px', marginBottom: '4px' }}>
                ℹ️ Logros asignados: {Object.values(evaluaciones).filter(evaluacion =>
                  evaluacion.destacado || evaluacion.el_jugon || evaluacion.el_cerrojo || evaluacion.el_lider ||
                  evaluacion.el_mejor_companero || evaluacion.el_machete || evaluacion.jugador_de_equipo
                ).length}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Controles globales */}
      <Card style={{
        marginBottom: isMobile ? '20px' : '24px',
        border: '2px solid #4352A9',
        borderRadius: '12px'
      }}>
        <Card.Header style={{
          backgroundColor: '#e3f2fd',
          color: '#1a1a1a',
          fontWeight: '700',
          fontSize: isMobile ? '16px' : '18px',
          border: '2px solid #4352A9',
          borderBottom: '4px solid #4352A9'
        }}>
          🎯 Controles Globales
        </Card.Header>
        <Card.Body style={{ padding: isMobile ? '16px' : '20px' }}>
          <Row>
            <Col md={6}>
              <h6 style={{ color: '#061156', marginBottom: '16px' }}>👥 Asistencia Masiva</h6>
              <Form.Check
                type="checkbox"
                id="asistencia-mi-equipo"
                label={`Marcar asistencia - Mi Equipo (${partido.miEquipo?.nombre})`}
                checked={asistenciaMasiva.miEquipo}
                onChange={(e) => handleAsistenciaMasiva('miEquipo', e.target.checked)}
                style={{ marginBottom: '12px' }}
              />
              <Form.Check
                type="checkbox"
                id="asistencia-equipo-rival"
                label={`Marcar asistencia - Equipo Rival (${partido.equipoRival?.nombre})`}
                checked={asistenciaMasiva.equipoRival}
                onChange={(e) => handleAsistenciaMasiva('equipoRival', e.target.checked)}
              />
            </Col>
            <Col md={6}>
              <h6 style={{ color: '#061156', marginBottom: '16px' }}>🏆 Resultado del Partido</h6>
              <Form.Select
                value={equipoGanador || ''}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === 'empate') {
                    handleEquipoGanador('empate');
                  } else if (value) {
                    handleEquipoGanador(parseInt(value));
                  } else {
                    handleEquipoGanador(null);
                  }
                }}
                style={{ marginBottom: '12px' }}
              >
                <option value="">Seleccionar resultado...</option>
                <option value={partido.miEquipo?.id}>Victoria - Mi Equipo ({partido.miEquipo?.nombre})</option>
                <option value={partido.equipoRival?.id}>Victoria - Equipo Rival ({partido.equipoRival?.nombre})</option>
                <option value="empate">⚖️ Empate</option>
              </Form.Select>
              {equipoGanador && equipoGanador !== 'empate' && (
                <Alert variant="success" style={{ fontSize: '14px', padding: '8px 12px' }}>
                  ✅ Victoria asignada automáticamente a todos los jugadores del equipo ganador (+2 puntos)
                </Alert>
              )}
              {equipoGanador === 'empate' && (
                <Alert variant="info" style={{ fontSize: '14px', padding: '8px 12px' }}>
                  ⚖️ Empate - No se asignarán puntos por victoria a ningún equipo
                </Alert>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Botones de acción */}
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        marginBottom: isMobile ? '20px' : '24px'
      }}>
        <Button
          variant="success"
          size="lg"
          onClick={() => setShowConfirm(true)}
          disabled={saving}
          style={{
            padding: '12px 24px',
            fontWeight: '600'
          }}
        >
          {saving ? 'Guardando...' : 'Guardar ProPoints'}
        </Button>
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={() => navigate(`/coach/partidos/${id}`)}
          style={{
            padding: '12px 24px'
          }}
        >
          Cancelar
        </Button>
      </div>

      {/* Mi Equipo */}
      {partido.miEquipo && (
        <Card style={{
          marginBottom: isMobile ? '20px' : '24px',
          border: '2px solid #4352A9',
          borderRadius: '12px'
        }}>
          <Card.Header style={{
            backgroundColor: '#e3f2fd',
            color: '#1a1a1a',
            fontWeight: '700',
            fontSize: isMobile ? '16px' : '18px',
            border: '2px solid #4352A9',
            borderBottom: '4px solid #4352A9'
          }}>
            🏃‍♂️ Mi Equipo: <span style={{ color: '#4352A9', fontWeight: '800' }}>{partido.miEquipo.nombre}</span>
          </Card.Header>
          <Card.Body style={{ padding: isMobile ? '12px' : '16px' }}>
            {ordenarJugadoresPorPosicion(partido.miEquipo.jugadores).map((jugador, _index) => (
              <div
                key={jugador.id}
                style={{
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: getPosicionColor(jugador.posicionAsignada),
                  borderRadius: '12px',
                  border: `2px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`,
                  borderLeft: `6px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`
                }}
              >
                {/* Header del jugador */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <div>
                    <strong style={{ color: '#1a1a1a', fontSize: '16px' }}>{jugador.nombre}</strong>
                    {jugador.apodo && (
                      <span style={{ color: '#495057', marginLeft: '8px', fontWeight: '500' }}>
                        ({jugador.apodo})
                      </span>
                    )}
                    <div style={{
                      fontSize: '13px',
                      color: getPosicionBorderColor(jugador.posicionAsignada),
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {getPosicionTexto(jugador.posicionAsignada)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {jugador.esTitular && (
                      <Badge bg="success" style={{ fontSize: '10px' }}>
                        TITULAR
                      </Badge>
                    )}
                    <Badge
                      style={{
                        backgroundColor: getPosicionBorderColor(jugador.posicionAsignada),
                        fontSize: '10px',
                        color: 'white'
                      }}
                    >
                      {getPosicionTexto(jugador.posicionAsignada).toUpperCase()}
                    </Badge>
                  </div>
                </div>

                {/* Controles básicos */}
                <Row style={{ marginBottom: '16px' }}>
                  <Col xs={6} md={3}>
                    <Form.Check
                      type="checkbox"
                      id={`asistencia-${jugador.id}`}
                      label="Asistencia (+1)"
                      checked={evaluaciones[jugador.id]?.asistencia || false}
                      onChange={(e) => handleEvaluacionChange(jugador.id, 'asistencia', e.target.checked)}
                    />
                  </Col>
                  <Col xs={6} md={3}>
                    <Form.Check
                      type="checkbox"
                      id={`destacado-${jugador.id}`}
                      label="Destacado (+2)"
                      checked={evaluaciones[jugador.id]?.destacado || false}
                      onChange={(e) => handleEvaluacionChange(jugador.id, 'destacado', e.target.checked)}
                    />
                  </Col>
                  <Col xs={6} md={3}>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      Victoria: {evaluaciones[jugador.id]?.victoria ? '✅ (+2)' : '❌'}
                    </div>
                  </Col>
                </Row>

                {/* Logros individuales */}
                <div style={{ marginBottom: '16px' }}>
                  <h6 style={{ color: '#061156', marginBottom: '8px', fontSize: '14px' }}>🏆 Logros Individuales</h6>
                  <div style={{
                    fontSize: '12px',
                    color: '#6c757d',
                    fontStyle: 'italic',
                    marginBottom: '12px'
                  }}>
                    ℹ️ Destacado: máximo 3 por partido | Otros logros: solo 1 por partido
                  </div>
                  <Row>
                    {logrosIndividuales.slice(1).map((logro) => (
                      <Col xs={6} md={4} lg={3} key={logro.key} style={{ marginBottom: '8px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: evaluaciones[jugador.id]?.[logro.key] ? '#d4edda' : '#f8f9fa',
                          borderRadius: '6px',
                          border: evaluaciones[jugador.id]?.[logro.key] ? '2px solid #28a745' : '1px solid #e1e5e9',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleEvaluacionChange(jugador.id, logro.key, !evaluaciones[jugador.id]?.[logro.key])}
                        >
                          <img
                            src={logro.imagen}
                            alt={logro.nombre}
                            style={{
                              width: '24px',
                              height: '24px',
                              marginRight: '8px',
                              borderRadius: '4px'
                            }}
                          />
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '600' }}>{logro.nombre}</div>
                            <div style={{ fontSize: '10px', color: '#6c757d' }}>+{logro.puntos} pts</div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Observaciones */}
                <Form.Group>
                  <Form.Label style={{ fontSize: '14px', fontWeight: '600', color: '#061156' }}>
                    📝 Observaciones
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Comentarios adicionales sobre el rendimiento del jugador..."
                    value={evaluaciones[jugador.id]?.observaciones || ''}
                    onChange={(e) => handleEvaluacionChange(jugador.id, 'observaciones', e.target.value)}
                    style={{ fontSize: '14px' }}
                  />
                </Form.Group>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {/* Equipo Rival */}
      {partido.equipoRival && (
        <Card style={{
          marginBottom: isMobile ? '20px' : '24px',
          border: '2px solid #dc3545',
          borderRadius: '12px'
        }}>
          <Card.Header style={{
            backgroundColor: '#ffebee',
            color: '#1a1a1a',
            fontWeight: '700',
            fontSize: isMobile ? '16px' : '18px',
            border: '2px solid #dc3545',
            borderBottom: '4px solid #dc3545'
          }}>
            ⚔️ Equipo Rival: <span style={{ color: '#dc3545', fontWeight: '800' }}>{partido.equipoRival.nombre}</span>
          </Card.Header>
          <Card.Body style={{ padding: isMobile ? '12px' : '16px' }}>
            {ordenarJugadoresPorPosicion(partido.equipoRival.jugadores).map((jugador, _index) => (
              <div
                key={jugador.id}
                style={{
                  padding: '16px',
                  marginBottom: '16px',
                  backgroundColor: getPosicionColor(jugador.posicionAsignada),
                  borderRadius: '12px',
                  border: `2px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`,
                  borderLeft: `6px solid ${getPosicionBorderColor(jugador.posicionAsignada)}`,
                  opacity: 0.9
                }}
              >
                {/* Header del jugador */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <div>
                    <strong style={{ color: '#1a1a1a', fontSize: '16px' }}>{jugador.nombre}</strong>
                    {jugador.apodo && (
                      <span style={{ color: '#495057', marginLeft: '8px', fontWeight: '500' }}>
                        ({jugador.apodo})
                      </span>
                    )}
                    <div style={{
                      fontSize: '13px',
                      color: getPosicionBorderColor(jugador.posicionAsignada),
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {getPosicionTexto(jugador.posicionAsignada)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {jugador.esTitular && (
                      <Badge bg="success" style={{ fontSize: '10px' }}>
                        TITULAR
                      </Badge>
                    )}
                    <Badge
                      style={{
                        backgroundColor: getPosicionBorderColor(jugador.posicionAsignada),
                        fontSize: '10px',
                        color: 'white'
                      }}
                    >
                      {getPosicionTexto(jugador.posicionAsignada).toUpperCase()}
                    </Badge>
                  </div>
                </div>

                {/* Controles básicos */}
                <Row style={{ marginBottom: '16px' }}>
                  <Col xs={6} md={3}>
                    <Form.Check
                      type="checkbox"
                      id={`asistencia-rival-${jugador.id}`}
                      label="Asistencia (+1)"
                      checked={evaluaciones[jugador.id]?.asistencia || false}
                      onChange={(e) => handleEvaluacionChange(jugador.id, 'asistencia', e.target.checked)}
                    />
                  </Col>
                  <Col xs={6} md={3}>
                    <Form.Check
                      type="checkbox"
                      id={`destacado-rival-${jugador.id}`}
                      label="Destacado (+2)"
                      checked={evaluaciones[jugador.id]?.destacado || false}
                      onChange={(e) => handleEvaluacionChange(jugador.id, 'destacado', e.target.checked)}
                    />
                  </Col>
                  <Col xs={6} md={3}>
                    <div style={{ fontSize: '12px', color: '#6c757d' }}>
                      Victoria: {evaluaciones[jugador.id]?.victoria ? '✅ (+2)' : '❌'}
                    </div>
                  </Col>
                </Row>

                {/* Logros individuales */}
                <div style={{ marginBottom: '16px' }}>
                  <h6 style={{ color: '#061156', marginBottom: '8px', fontSize: '14px' }}>🏆 Logros Individuales</h6>
                  <div style={{
                    fontSize: '12px',
                    color: '#6c757d',
                    fontStyle: 'italic',
                    marginBottom: '12px'
                  }}>
                    ℹ️ Destacado: máximo 3 por partido | Otros logros: solo 1 por partido
                  </div>
                  <Row>
                    {logrosIndividuales.slice(1).map((logro) => (
                      <Col xs={6} md={4} lg={3} key={logro.key} style={{ marginBottom: '8px' }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          backgroundColor: evaluaciones[jugador.id]?.[logro.key] ? '#d4edda' : '#f8f9fa',
                          borderRadius: '6px',
                          border: evaluaciones[jugador.id]?.[logro.key] ? '2px solid #28a745' : '1px solid #e1e5e9',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleEvaluacionChange(jugador.id, logro.key, !evaluaciones[jugador.id]?.[logro.key])}
                        >
                          <img
                            src={logro.imagen}
                            alt={logro.nombre}
                            style={{
                              width: '24px',
                              height: '24px',
                              marginRight: '8px',
                              borderRadius: '4px'
                            }}
                          />
                          <div>
                            <div style={{ fontSize: '12px', fontWeight: '600' }}>{logro.nombre}</div>
                            <div style={{ fontSize: '10px', color: '#6c757d' }}>+{logro.puntos} pts</div>
                          </div>
                        </div>
                      </Col>
                    ))}
                  </Row>
                </div>

                {/* Observaciones */}
                <Form.Group>
                  <Form.Label style={{ fontSize: '14px', fontWeight: '600', color: '#061156' }}>
                    📝 Observaciones
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="Comentarios adicionales sobre el rendimiento del jugador..."
                    value={evaluaciones[jugador.id]?.observaciones || ''}
                    onChange={(e) => handleEvaluacionChange(jugador.id, 'observaciones', e.target.value)}
                    style={{ fontSize: '14px' }}
                  />
                </Form.Group>
              </div>
            ))}
          </Card.Body>
        </Card>
      )}

      {/* Botones de acción finales */}
      <div style={{
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
        marginBottom: isMobile ? '20px' : '24px'
      }}>
        <Button
          variant="success"
          size="lg"
          onClick={() => setShowConfirm(true)}
          disabled={saving}
          style={{
            padding: '12px 24px',
            fontWeight: '600'
          }}
        >
          {saving ? 'Guardando...' : 'Guardar ProPoints'}
        </Button>
        <Button
          variant="outline-secondary"
          size="lg"
          onClick={() => navigate(`/coach/partidos/${id}`)}
          style={{
            padding: '12px 24px'
          }}
        >
          Cancelar
        </Button>
      </div>

      {/* Modal de confirmación custom */}
      {showConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1050,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
            maxWidth: '500px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            {/* Header */}
            <div style={{
              padding: '24px 24px 16px 24px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h4 style={{
                margin: 0,
                color: '#061156',
                fontSize: '20px',
                fontWeight: '700'
              }}>
                ✅ Confirmar ProPoints
              </h4>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  color: '#6c757d',
                  cursor: 'pointer',
                  padding: '0',
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '50%'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                ×
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '24px' }}>
              <p style={{
                margin: '0 0 12px 0',
                fontSize: '16px',
                color: '#495057',
                lineHeight: '1.5'
              }}>
                ¿Estás seguro de que quieres guardar las evaluaciones ProPoints para este partido?
              </p>
              <p style={{
                margin: '0 0 20px 0',
                fontSize: '14px',
                color: '#dc3545',
                fontWeight: '600'
              }}>
                ⚠️ Esta acción no se puede deshacer.
              </p>

              {/* Resumen de evaluaciones */}
              <div style={{
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                padding: '16px',
                border: '1px solid #e9ecef'
              }}>
                <h6 style={{
                  margin: '0 0 12px 0',
                  color: '#061156',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  📊 Resumen de evaluaciones:
                </h6>
                <div style={{ fontSize: '14px', color: '#495057' }}>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>👥 Jugadores con asistencia:</strong> {Object.values(evaluaciones).filter(e => e.asistencia).length}
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>🏆 Jugadores con victoria:</strong> {Object.values(evaluaciones).filter(e => e.victoria).length}
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>⭐ Jugadores destacados:</strong> {Object.values(evaluaciones).filter(e => e.destacado).length}
                  </div>
                  <div style={{ marginBottom: '6px' }}>
                    <strong>🎯 Logros individuales:</strong> {Object.values(evaluaciones).filter(e => e.el_jugon || e.el_cerrojo || e.el_lider || e.el_mejor_companero || e.el_machete).length}
                  </div>
                  <div>
                    <strong>🏅 Equipo ganador:</strong> {equipoGanador ?
                      (equipoGanador === partido.miEquipo?.id ? partido.miEquipo?.nombre : partido.equipoRival?.nombre)
                      : 'No seleccionado'}
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              padding: '16px 24px 24px 24px',
              borderTop: '1px solid #e9ecef',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <button
                onClick={() => setShowConfirm(false)}
                style={{
                  padding: '10px 20px',
                  border: '1px solid #6c757d',
                  backgroundColor: 'white',
                  color: '#6c757d',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#6c757d';
                  e.target.style.color = 'white';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'white';
                  e.target.style.color = '#6c757d';
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleGuardarEvaluaciones}
                disabled={saving}
                style={{
                  padding: '10px 20px',
                  border: 'none',
                  backgroundColor: saving ? '#6c757d' : '#28a745',
                  color: 'white',
                  borderRadius: '6px',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s',
                  opacity: saving ? 0.7 : 1
                }}
                onMouseOver={(e) => {
                  if (!saving) {
                    e.target.style.backgroundColor = '#218838';
                  }
                }}
                onMouseOut={(e) => {
                  if (!saving) {
                    e.target.style.backgroundColor = '#28a745';
                  }
                }}
              >
                {saving ? '⏳ Guardando...' : '✅ Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para mostrar el partido finalizado con badges de logros
const PartidoFinalizadoView = ({ partido, navigate }) => {
  const [logrosAsignados, setLogrosAsignados] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const cargarLogrosAsignados = async () => {
      try {
        const token = localStorage.getItem('prokids_token');
        const response = await fetch(`/api/coach/partidos/${partido.id}/logros`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const data = await response.json();
          setLogrosAsignados(data.logros || {});
        } else {
          console.error('❌ Error al cargar logros:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('❌ Error al cargar logros asignados:', error);
      } finally {
        setLoading(false);
      }
    };

    if (partido?.id) {
      cargarLogrosAsignados();
    } else {
      setLoading(false);
    }
  }, [partido]);

  const getBadgeStyle = (logro) => {
    const baseStyle = {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '12px',
      fontSize: '12px',
      fontWeight: 'bold',
      margin: '2px',
      color: 'white'
    };

    switch (logro) {
      case 'asistencia':
        return { ...baseStyle, backgroundColor: '#17a2b8' };
      case 'victoria':
        return { ...baseStyle, backgroundColor: '#28a745' };
      case 'destacado':
        return { ...baseStyle, backgroundColor: '#ffc107', color: '#212529' };
      case 'El Jugón':
        return { ...baseStyle, backgroundColor: '#dc3545' };
      case 'El Cerrojo':
        return { ...baseStyle, backgroundColor: '#6f42c1' };
      case 'El Desequilibrante':
        return { ...baseStyle, backgroundColor: '#fd7e14' };
      case 'El Líder':
        return { ...baseStyle, backgroundColor: '#20c997' };
      case 'El Mejor Compañero':
        return { ...baseStyle, backgroundColor: '#e83e8c' };
      case 'Jugador de Equipo':
        return { ...baseStyle, backgroundColor: '#007bff' };
      default:
        return { ...baseStyle, backgroundColor: '#6c757d' };
    }
  };

  const getLogroEmoji = (logro) => {
    switch (logro) {
      case 'asistencia': return '👥';
      case 'victoria': return '🏆';
      case 'destacado': return '⭐';
      case 'El Jugón': return '🎮';
      case 'El Cerrojo': return '🛡️';
      case 'El Desequilibrante': return '⚡';
      case 'El Líder': return '👑';
      case 'El Mejor Compañero': return '🤝';
      case 'Jugador de Equipo': return '🤝';
      default: return '🏅';
    }
  };

  const renderJugadorConLogros = (jugador) => {
    const jugadorLogros = logrosAsignados[jugador.id] || [];
    const totalPuntos = jugadorLogros.reduce((sum, logro) => sum + logro.puntos, 0);

    return (
      <div key={jugador.id} style={{
        backgroundColor: '#f8f9fa',
        border: '1px solid #dee2e6',
        borderRadius: '8px',
        padding: '15px',
        marginBottom: '10px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px'
        }}>
          <div>
            <strong>{jugador.nombre}</strong>
            {jugador.apodo && (
              <span style={{ color: '#666', marginLeft: '8px' }}>
                &ldquo;{jugador.apodo}&rdquo;
              </span>
            )}
          </div>
          <div style={{
            backgroundColor: '#007bff',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: 'bold'
          }}>
            {totalPuntos} pts
          </div>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
          {jugadorLogros.map((logro, index) => (
            <span
              key={index}
              style={getBadgeStyle(logro.logro_nombre)}
              title={`${logro.logro_nombre} (+${logro.puntos} pts)`}
            >
              {getLogroEmoji(logro.logro_nombre)} {logro.logro_nombre} (+{logro.puntos})
            </span>
          ))}
          {jugadorLogros.length === 0 && (
            <span style={{ color: '#666', fontStyle: 'italic' }}>
              Sin logros asignados
            </span>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '18px', color: '#666' }}>
          Cargando logros del partido...
        </div>
      </div>
    );
  }

  // Verificar si tenemos los datos necesarios
  if (!partido.miEquipo || !partido.equipoRival) {
    console.error('❌ Faltan datos de equipos:', { miEquipo: partido.miEquipo, equipoRival: partido.equipoRival });
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h1>🔔 PARTIDO FINALIZADO</h1>
        <div style={{ color: 'red', marginBottom: '20px' }}>
          Error: Faltan datos de los equipos
        </div>
        <button onClick={() => navigate('/coach/partidos')}>
          Volver a Partidos
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header del partido finalizado */}
      <div style={{
        backgroundColor: '#f8f9fa',
        border: '2px solid #28a745',
        borderRadius: '10px',
        padding: '20px',
        marginBottom: '30px',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '48px', marginBottom: '15px' }}>🔔</div>
        <h2 style={{ color: '#28a745', marginBottom: '10px' }}>
          Partido Finalizado
        </h2>
        <div style={{
          backgroundColor: '#e9ecef',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '15px'
        }}>
          <strong>Estado:</strong> {partido.estado}<br/>
          <strong>Equipo Ganador:</strong> {
            partido.equipoGanador
              ? (partido.miEquipo.id === partido.equipoGanador
                  ? partido.miEquipo.nombre
                  : partido.equipoRival.nombre)
              : 'No especificado'
          }
        </div>
        <button
          onClick={() => navigate('/coach/partidos')}
          style={{
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            padding: '12px 24px',
            borderRadius: '6px',
            fontSize: '16px',
            cursor: 'pointer'
          }}
        >
          Volver a Mis Partidos
        </button>
      </div>

      {/* Logros por equipo */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '30px' }}>
        {/* Mi Equipo */}
        <div>
          <h3 style={{
            color: '#007bff',
            marginBottom: '20px',
            textAlign: 'center',
            padding: '10px',
            backgroundColor: '#e3f2fd',
            borderRadius: '8px'
          }}>
            🏠 {partido.miEquipo.nombre}
          </h3>
          {partido.miEquipo.jugadores && partido.miEquipo.jugadores.length > 0
            ? partido.miEquipo.jugadores.map(renderJugadorConLogros)
            : <div style={{ textAlign: 'center', color: '#666' }}>No hay jugadores</div>
          }
        </div>

        {/* Equipo Rival */}
        <div>
          <h3 style={{
            color: '#dc3545',
            marginBottom: '20px',
            textAlign: 'center',
            padding: '10px',
            backgroundColor: '#ffebee',
            borderRadius: '8px'
          }}>
            🚀 {partido.equipoRival.nombre}
          </h3>
          {partido.equipoRival.jugadores && partido.equipoRival.jugadores.length > 0
            ? partido.equipoRival.jugadores.map(renderJugadorConLogros)
            : <div style={{ textAlign: 'center', color: '#666' }}>No hay jugadores</div>
          }
        </div>
      </div>
    </div>
  );
};

export default CoachAsignarProPoints;
